import {
  isValidUaIban,
  KVED_PATTERN,
  normalizeIban,
  SWIFT_PATTERN,
  TAX_ID_PATTERN,
  type PaymentCurrency,
} from '@hygge/shared';
import type { PaymentDetails, PaymentDetailsInput } from '@/lib/employee-finance';

/**
 * The FOP payment details dialog's rules [HW-1008]. Kept out of the component
 * because the repo's vitest runs in a node environment — same reasoning as
 * profile-tabs.ts. The validators come from @hygge/shared and the messages
 * repeat UpsertPaymentDetailsDto's, so the text under a field matches a 400.
 */

/** The dialog's raw inputs — all strings, KVED as free text. */
export interface PaymentDetailsForm {
  fopName: string;
  iban: string;
  taxId: string;
  swift: string;
  /** '' = none */
  currency: string;
  registrationAddress: string;
  kved: string;
}

export type PaymentDetailsErrors = Partial<Record<keyof PaymentDetailsForm, string>>;

/** KVED codes typed as "62.01, 62.02" or one per line. */
export function parseKved(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// The API's limits (UpsertPaymentDetailsDto), checked on the trimmed text it stores.
const MAX_FOP_NAME = 300;
const MAX_ADDRESS = 1000;
const MAX_KVED_CODES = 50;

export function validatePaymentDetails(form: PaymentDetailsForm): PaymentDetailsErrors {
  const errors: PaymentDetailsErrors = {};
  const fopName = form.fopName.trim();
  if (!fopName) errors.fopName = 'FOP name is required';
  else if (fopName.length > MAX_FOP_NAME) {
    errors.fopName = `FOP name must be at most ${MAX_FOP_NAME} characters`;
  }
  if (form.iban.trim() && !isValidUaIban(form.iban)) {
    errors.iban = 'IBAN must be UA followed by 27 characters with a valid checksum';
  }
  if (form.taxId.trim() && !TAX_ID_PATTERN.test(form.taxId.trim())) {
    errors.taxId = 'Tax ID must be exactly 10 digits';
  }
  if (form.swift.trim() && !SWIFT_PATTERN.test(form.swift.trim().toUpperCase())) {
    errors.swift = 'SWIFT/BIC must be 8 or 11 characters';
  }
  if (form.registrationAddress.trim().length > MAX_ADDRESS) {
    errors.registrationAddress = `Registration address must be at most ${MAX_ADDRESS} characters`;
  }
  const kved = parseKved(form.kved);
  // Counted after dropping repeats, as the API counts them.
  if (new Set(kved).size > MAX_KVED_CODES) errors.kved = `At most ${MAX_KVED_CODES} KVED codes`;
  else if (kved.some((code) => !KVED_PATTERN.test(code))) {
    errors.kved = 'Each KVED code must look like 62.01';
  }
  return errors;
}

/**
 * How a server 400's messages name each field, for `serverFieldErrors`: the
 * property name class-validator starts with, and the label the DTO's custom
 * messages start with.
 */
export const PAYMENT_DETAILS_SERVER_FIELDS: Record<keyof PaymentDetailsForm, readonly string[]> = {
  fopName: ['fopName ', 'FOP name '],
  iban: ['iban ', 'IBAN '],
  taxId: ['taxId ', 'Tax ID '],
  swift: ['swift ', 'SWIFT/BIC '],
  currency: ['currency '],
  registrationAddress: ['registrationAddress ', 'Registration address '],
  kved: ['kved ', 'each value in kved ', 'Each KVED code '],
};

const orNull = (value: string) => value.trim() || null;

/** The PUT body: the whole block, blanks as null (PUT clears what is not sent). */
export function toPaymentDetailsInput(form: PaymentDetailsForm): PaymentDetailsInput {
  return {
    fopName: form.fopName.trim(),
    iban: form.iban.trim() ? normalizeIban(form.iban) : null,
    taxId: orNull(form.taxId),
    swift: form.swift.trim() ? form.swift.trim().toUpperCase() : null,
    currency: (orNull(form.currency) as PaymentCurrency | null) ?? null,
    registrationAddress: orNull(form.registrationAddress),
    kved: parseKved(form.kved),
  };
}

export function toPaymentDetailsForm(
  details: PaymentDetails | null | undefined
): PaymentDetailsForm {
  return {
    fopName: details?.fopName ?? '',
    iban: details?.iban ?? '',
    taxId: details?.taxId ?? '',
    swift: details?.swift ?? '',
    currency: details?.currency ?? '',
    registrationAddress: details?.registrationAddress ?? '',
    kved: details?.kved.join(', ') ?? '',
  };
}

/** "UA21 3223 1300 …" — grouped by four, as people read and dictate an IBAN. */
export function formatIban(iban: string): string {
  return iban.replace(/(.{4})(?=.)/g, '$1 ');
}
